import { useRef, useState } from "react";
import { BottomSheet } from "../../shared/BottomSheet";
import { AddKindRow } from "../../shared/AddSheet";
import { ADD_KINDS } from "../../shared/addKinds";
import { C } from "../../shared/theme";
import { categoryLabel } from "../../shared/categories";
import { COMPASS_KINDS } from "./compassKinds";
import { habitsForGoal, habitsToLink, linkHabitToGoal } from "./compassLists";
import { CategoryItemEditor } from "./CategoryItemEditor";
import { CompassRow } from "./CompassParts";
import { listCard } from "../../shared/ListRow";
import { sectionLabel } from "../../shared/textStyles";
import { AddItemButton } from "../../shared/AddItemButton";

const HABITS = COMPASS_KINDS.habits;

// The Goal page's Habits: the habits that serve this goal, from any category (Craig, 24 Oct 2026: "add a Habits
// section to the Goal page so I can see them right there"). Tap one to change it here. + Add offers the habits you
// already have (Craig, 24 Oct 2026: "I would like a habit picker on the Goal page to add existing habits") and a new
// one, made with this goal already chosen, in the goal's category when it has one. With no habits to choose from it
// goes straight to the new one.
export function GoalHabits({ goal, goals, compass, setCompass }) {
  const categories = compass?.categories || [];
  const habits = habitsForGoal(categories, goal.id);
  const [editing, setEditing] = useState(null); // null, "new", or { categoryId, index }
  const [picking, setPicking] = useState(false);
  const offered = habitsToLink(categories, goal.id);
  const goalTitle = (id) => goals.find(g => g.id === id)?.title;
  const from = editing === "new" ? null : editing;
  const save = (change) => setCompass(p => ({ ...p, categories: change(p.categories || []) }));
  const goalCategory = categories.some(c => c.id === goal.categoryId) ? goal.categoryId : (categories.length === 1 ? categories[0].id : "");

  // A habit needs a category, so with none there is nothing to show or add here.
  if (categories.length === 0) return null;
  return (
    <section aria-labelledby="goal-habits" style={{ margin: "20px 20px 8px" }}>
      {/* Laid out as the page's Tasks are (Craig, 30 Oct 2026: "Yes, good idea"): the heading, the habits, then
          "+ Add a habit" under them. With none, only the heading and "+ Add a habit" show. */}
      <h2 id="goal-habits" style={{ ...sectionLabel(), marginBottom: 10 }}>Habits</h2>
      {habits.length > 0 && (
        <div style={listCard()}>
          {habits.map(({ item, text, categoryId, index }, i) => (
            <CompassRow key={`${categoryId}-${index}`} habit={item} text={text} first={i === 0} editLabel="Edit habit"
              onEdit={() => setEditing({ categoryId, index })} />
          ))}
        </div>
      )}
      <AddItemButton text="+ Add a habit" label={`Add a habit for ${goal.title}`} onClick={() => (offered.length ? setPicking(true) : setEditing("new"))} />
      {picking && (
        <HabitPicker goal={goal} offered={offered} goalTitle={goalTitle} onClose={() => setPicking(false)}
          onNew={() => { setPicking(false); setEditing("new"); }}
          onPick={(where) => { save(cats => linkHabitToGoal(cats, where, goal.id)); setPicking(false); }} />
      )}
      {editing !== null && (
        <CategoryItemEditor kind={HABITS} categories={categories} goals={goals} from={from}
          initialCategoryId={goalCategory} initialGoalId={goal.id} setCompass={setCompass}
          onClose={() => setEditing(null)} />
      )}
    </section>
  );
}

const HABIT_KIND = ADD_KINDS.find(k => k.id === "habit");

// + Add's sheet: a new habit first, drawn as + → Add new draws it, then every habit not already for this goal, under
// its category, in the rows the Habits page uses. One that serves another goal says so, since choosing it moves it.
function HabitPicker({ goal, offered, goalTitle, onNew, onPick, onClose }) {
  const startRef = useRef(null);
  return (
    <BottomSheet labelledBy="habit-picker-title" startRef={startRef} onClose={onClose}>
      <h2 id="habit-picker-title" style={{ margin: "0 0 10px", fontSize: 17, fontWeight: 700, lineHeight: 1.35 }}>Add a habit for {goal.title}</h2>
      <AddKindRow kind={HABIT_KIND} title="New habit" buttonRef={startRef} onClick={onNew} />
      <p style={{ margin: "18px 4px 8px", fontSize: 14, lineHeight: 1.5, color: C.textMid }}>Or choose one you already have:</p>
      {offered.map(({ category, items }) => (
        <div key={category.id} role="group" aria-labelledby={`habit-picker-${category.id}`} style={{ marginBottom: 12 }}>
          <h3 id={`habit-picker-${category.id}`} style={{ margin: "0 4px 6px", fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: C.textMid }}>
            {categoryLabel(category)}
          </h3>
          <div style={listCard()}>
            {items.map(({ item, text, index }, i) => {
              const other = item?.goalId ? goalTitle(item.goalId) : null;
              return (
                <CompassRow key={index} habit={item} text={text} first={i === 0} chooseOnly
                  note={other ? `This habit is for ${other}. Choosing it moves it to ${goal.title}.` : null}
                  onEdit={() => onPick({ categoryId: category.id, index, text })} />
              );
            })}
          </div>
        </div>
      ))}
    </BottomSheet>
  );
}
